import React from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { FolderNode } from '../../types/fileSystem.js';
import type { RagTableType } from '../../types/rag.js';
import { useDeleteFolders } from './FileSystem/FolderSection/useDeleteFolders.js';
import { useDocumentActions } from './useDocumentActions.js';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  documents: RagTableType[];
  folders?: FolderNode[];
  onUnselectTableRows: () => void;
};

const DeleteNodesDialog = ({
  isOpen,
  onClose,
  onUnselectTableRows,
  documents,
  folders = [],
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { onDocumentsBulkDelete } = useDocumentActions();
  const { deleteFolders } = useDeleteFolders();

  // A single node is named outright; larger selections fall back to the noun
  // that covers what is actually being removed.
  const getCopy = () => {
    if (folders.length === 1 && !documents.length) {
      const { name } = folders[0];

      return {
        title: t('Delete {{name}}?', { name }),
        description: t(
          'If you proceed {{name}} will be permanently deleted, along with all the files it contains.',
          { name },
        ),
      };
    }

    if (documents.length === 1 && !folders.length) {
      const name = documents[0].fileName;

      return {
        title: t('Delete {{name}}?', { name }),
        description: t(
          'If you proceed {{name}} will be permanently deleted from the Knowledge base.',
          { name },
        ),
      };
    }

    if (folders.length && documents.length) {
      return {
        title: t('Delete items?'),
        description: t(
          'If you proceed, the selected items will be permanently deleted, along with all the files they contain.',
        ),
      };
    }

    if (folders.length) {
      return {
        title: t('Delete folders?'),
        description: t(
          'If you proceed, the selected folders will be permanently deleted, along with all the files they contain.',
        ),
      };
    }

    return {
      title: t('Delete files?'),
      description: t(
        'If you proceed, the selected files will be permanently deleted from the Knowledge base.',
      ),
    };
  };

  const { title, description } = getCopy();

  const handleDelete = () => {
    if (!documents.length && !folders.length) return;

    if (folders.length) {
      deleteFolders(folders.map((folder) => folder.id));
    }

    if (documents.length) {
      onDocumentsBulkDelete(documents, onUnselectTableRows);
    }

    onUnselectTableRows();
    onClose();
  };

  return (
    <Dialog modal onOpenChange={onClose} open={isOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot aria-describedby={undefined} size="s">
          <DialogHeader>
            <DialogTitle className="flex flex-row items-center gap-2">
              <WarningAltFilled className="size-4 shrink-0 fill-error-main" />
              <p className="min-w-0 wrap-break-word">{title}</p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent>
            <div
              className={cn(
                typographyVariants({ variant: 'bodyLong01' }),
                'wrap-break-word',
              )}
            >
              {description}
            </div>
          </DialogContent>
          <DialogFooter className="flex flex-row items-center justify-end gap-2">
            <Button
              className="h-10 w-20"
              size="large"
              variant={'plain'}
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
            <Button
              color="error"
              size="large"
              onClick={() => handleDelete()}
              data-dd-action-name="Knowledge base: confirm delete"
            >
              {t('Yes, delete')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { DeleteNodesDialog };
